import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/New_petrotago.jfif';
import GoogleAsset from '../../assets/icons8-google 1.png';
import AzureAsset from '../../assets/Frame 59.png';
import { signup, verifySignupEmail, resendCode, ApiError, VerifySignupEmailError } from '../../utils/auth';
import { useCountdown } from '../../hooks/useCountdown';

const allCountries = Country.getAllCountries();

interface CountryOption {
  name: string;
  isoCode: string;
  flag: string;
}

interface StateOption {
  name: string;
  isoCode: string;
}

interface FormData {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  company_name: string;
  industry_type: string;
  company_registration_number: string;
  company_address: string;
  country: string;
  state_province: string;
  phone_number: string;
  website: string;
}

const INDUSTRY_OPTIONS = [
  { value: 'oil_and_gas', label: 'Oil & Gas' },
  { value: 'mining', label: 'Mining' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'other', label: 'Other' },
];

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    company_name: '',
    industry_type: 'oil_and_gas',
    company_registration_number: '',
    company_address: '',
    country: '',
    state_province: '',
    phone_number: '',
    website: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({
    open: false, message: '', severity: 'error',
  });
  const showToast = (message: string, severity: 'error' | 'success' = 'error') =>
    setToast({ open: true, message, severity });
  const closeToast = () => setToast(prev => ({ ...prev, open: false }));
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [availableStates, setAvailableStates] = useState<StateOption[]>([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  // One emailed code per 60 s; a 429 carries retryAfter for the countdown.
  const resendWait = useCountdown();

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const validateStep1 = () => {
    if (!formData.username) return 'Username is required';
    if (!formData.email) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Enter a valid email address';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 8) return 'Password must be at least 8 characters';
    if (formData.password !== formData.confirm_password) return 'Passwords do not match';
    return '';
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { showToast(err); return; }
    setStep(2);
  };

  const handleNext2 = () => {
    if (!formData.company_name.trim()) { showToast('Company name is required'); return; }
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!formData.country) { showToast('Country is required'); return; }
    if (!agreed) { showToast('You must agree to the Terms of Service and Privacy Policy'); return; }

    setLoading(true);
    try {
      await signup(formData);
      setStep(4);
      showToast(`We emailed a verification code to ${formData.email}.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      showToast(apiErr?.message ?? 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verifyCode.trim()) { showToast('Enter the code we emailed you.'); return; }
    setVerifyLoading(true);
    try {
      await verifySignupEmail(formData.email, verifyCode.trim());
      showToast('Email verified! Redirecting to login…', 'success');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      const apiErr = err as VerifySignupEmailError;
      const remaining = apiErr?.attemptsRemaining ?? apiErr?.trialsRemaining;
      const suffix = remaining != null ? ` (${remaining} attempt${remaining === 1 ? '' : 's'} remaining)` : '';
      showToast((apiErr?.message ?? 'Invalid or expired code.') + suffix);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendLoading || resendWait.remaining > 0) return;
    setResendLoading(true);
    try {
      await resendCode(formData.email, 'signup');
      resendWait.start(60);
      showToast(`Sent a new code to ${formData.email}.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 429) resendWait.start(apiErr.retryAfter ?? 60);
      showToast(apiErr?.message ?? 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.925rem',
      height: '48px',
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(180, 195, 215, 1)', borderWidth: '1px' },
      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(43,103,176,0.14)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(43,103,176,1) !important', borderWidth: '1.5px !important' },
      '& input': { padding: '13px 14px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    fontSize: '0.925rem',
    height: '48px',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(180, 195, 215, 1)', borderWidth: '1px' },
    '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(43,103,176,0.14)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(43,103,176,1) !important', borderWidth: '1.5px !important' },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  const colGap = 1.5;

  return (
    <Box sx={{ display: 'flex', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 100% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(circle 420px at 4% 8%, rgba(43,103,176,0.18), rgba(43,103,176,0) 75%),
            radial-gradient(circle 500px at 8% 98%, rgba(30,58,95,0.16), rgba(30,58,95,0) 75%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, auto, auto, 26px 26px',
          backgroundPosition: 'right center, 0 0, 0 0, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat, repeat',
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: { xs: '20px', sm: '32px', md: '48px' },
          px: { xs: 2, sm: 3 },
        }}
      >
      <Box
        sx={{
          my: 'auto',
          width: { xs: '100%', sm: '440px', md: '452px' },
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2.5, sm: 4, md: 5 },
          bgcolor: '#ffffff',
          borderRadius: '22px',
          border: '1px solid rgba(226,232,240,0.7)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.03), 0 10px 22px -14px rgba(30,58,95,0.12)',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: { xs: 48, sm: 56, md: 80 }, maxWidth: '100%', objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {/* ── STEP 1: Account Details ─────────────────────────────── */}
        {step === 1 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1 }}>
                <Box sx={{ width: 26, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, rgba(43,103,176,1), rgba(27,72,132,1))' }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(43,103,176,1)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Fleet &amp; Fuel Intelligence
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 400,
                  fontSize: '22px',
                  lineHeight: '48px',
                  color: '#0F1D30',
                }}
              >
                Welcome to
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '26px', md: '33.31px' },
                  lineHeight: { xs: '36px', md: '48px' },
                  color: 'rgba(43,103,176,1)',
                  mt: -0.5,
                }}
              >
                PetroTag360!
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                Fuel tracking made transparent, smart, and reliable
              </Typography>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>

              {/* Username */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Username</Typography>
                <TextField fullWidth placeholder="Username" size="small"
                  value={formData.username} onChange={handleChange('username')} sx={inputSx} />
              </Box>

              {/* Email */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Email address</Typography>
                <TextField fullWidth placeholder="Enter your email" type="email" size="small"
                  value={formData.email} onChange={handleChange('email')} sx={inputSx} />
              </Box>

              {/* Password */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Password</Typography>
                <TextField
                  fullWidth placeholder="Password" size="small"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} onChange={handleChange('password')} sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Confirm Password */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={labelSx}>Confirm Password</Typography>
                <TextField
                  fullWidth placeholder="Confirm password" size="small"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirm_password} onChange={handleChange('confirm_password')} sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                          {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Next button */}
              <Button
                fullWidth type="submit" variant="contained"
                sx={{
                  height: '48px',
                  background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
                  borderRadius: '11px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  mb: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                    boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Next →
              </Button>

              {/* Google/Azure signup temporarily disabled — flip this back to `true` to restore */}
              {false && (
              <>
              {/* Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
                <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', whiteSpace: 'nowrap' }}>Or</Typography>
                <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
              </Box>

              {/* Social signup */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: colGap, mb: 1.5 }}>
                <Button variant="outlined"
                  startIcon={<Box component="img" src={GoogleAsset} alt="Google" sx={{ width: 18, height: 18 }} />}
                  sx={{
                    height: '43px', borderColor: '#E5E7EB', color: '#374151',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 500,
                    fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
                    '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Sign up with Google
                </Button>
                <Button variant="outlined"
                  startIcon={<Box component="img" src={AzureAsset} alt="Azure" sx={{ width: 18, height: 18 }} />}
                  sx={{
                    height: '43px', borderColor: '#E5E7EB', color: '#374151',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 500,
                    fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
                    '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Sign up with Azure
                </Button>
              </Box>
              </>
              )}

              {/* Login link */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: 1.5, letterSpacing: '0%', color: '#6B7280' }}>
                  Have an account already?{' '}
                  <Link component="button" type="button" onClick={() => navigate('/')} underline="hover"
                    sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: 1.5, letterSpacing: '0%', color: 'rgba(43, 103, 176, 1)', verticalAlign: 'baseline' }}>
                    Log in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ── STEP 2: Company Details ──────────────────────────────── */}
        {step === 2 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '22px', md: '26px' },
                  lineHeight: { xs: '32px', md: '40px' },
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                }}
              >
                Company Details
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: 1.5,
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                Tell us about your organisation
              </Typography>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleNext2(); }}>

              {/* Company Name */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Company Name</Typography>
                <TextField fullWidth placeholder="Company name" size="small"
                  value={formData.company_name} onChange={handleChange('company_name')} sx={inputSx} />
              </Box>

              {/* Industry Type */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Industry Type</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.industry_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry_type: e.target.value }))}
                    sx={selectSx}
                  >
                    {INDUSTRY_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Reg. Number */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Reg. Number</Typography>
                <TextField fullWidth placeholder="Registration number" size="small"
                  value={formData.company_registration_number} onChange={handleChange('company_registration_number')} sx={inputSx} />
              </Box>

              {/* Phone Number */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={labelSx}>Phone Number</Typography>
                <TextField fullWidth placeholder="+234 000 000 0000" size="small"
                  value={formData.phone_number} onChange={handleChange('phone_number')} sx={inputSx} />
              </Box>

              {/* Buttons row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: colGap }}>
                <Button variant="outlined" onClick={() => setStep(1)}
                  startIcon={<ArrowBack fontSize="small" />}
                  sx={{
                    height: '48px', minWidth: 0, px: { xs: 1, sm: 2 }, borderColor: 'rgba(217, 217, 217, 1)', color: 'rgba(0,0,0,0.7)',
                    borderRadius: '11px', textTransform: 'none', fontWeight: 600,
                    fontSize: '0.85rem', boxShadow: 'none',
                    '&:hover': { borderColor: '#C4C4C4', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth type="submit" variant="contained"
                  sx={{
                    height: '48px',
                    background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
                    borderRadius: '11px',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                      boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Next →
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* ── STEP 3: Location & Contact ───────────────────────────── */}
        {step === 3 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '22px', md: '26px' },
                  lineHeight: { xs: '32px', md: '40px' },
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                }}
              >
                Location &amp; Contact
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: 1.5,
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                Where should we reach you?
              </Typography>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

              {/* Country */}
              <Box sx={{ mb: 1.5 }}>
                  <Typography sx={labelSx}>Country</Typography>
                  <Autocomplete
                    options={allCountries}
                    getOptionLabel={(option) => option.name}
                    value={selectedCountry}
                    onChange={(_, newValue) => {
                      setSelectedCountry(newValue);
                      setSelectedState(null);
                      if (newValue) {
                        const states = State.getStatesOfCountry(newValue.isoCode).map(s => ({ name: s.name, isoCode: s.isoCode }));
                        setAvailableStates(states);
                        setFormData(prev => ({ ...prev, country: newValue.name, state_province: '' }));
                      } else {
                        setAvailableStates([]);
                        setFormData(prev => ({ ...prev, country: '', state_province: '' }));
                      }
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.85rem', gap: 1 }}>
                        <span style={{ fontSize: '1.1rem' }}>{option.flag}</span>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        size="small"
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            height: '48px',
                            flexWrap: 'nowrap',
                            '& input': { padding: '13px 8px' },
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: selectedCountry ? (
                            <InputAdornment position="start" sx={{ ml: 1 }}>
                              <span style={{ fontSize: '1.1rem' }}>{selectedCountry.flag}</span>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    )}
                  />
              </Box>

              {/* State / Province */}
              <Box sx={{ mb: 1.5 }}>
                  <Typography sx={labelSx}>State / Province</Typography>
                  <Autocomplete
                    options={availableStates}
                    getOptionLabel={(option) => option.name}
                    value={selectedState}
                    disabled={availableStates.length === 0}
                    onChange={(_, newValue) => {
                      setSelectedState(newValue);
                      setFormData(prev => ({ ...prev, state_province: newValue?.name || '' }));
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.85rem' }}>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={availableStates.length === 0 ? 'Select country first' : 'Select state'}
                        size="small"
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            height: '48px',
                            flexWrap: 'nowrap',
                            '& input': { padding: '13px 8px' },
                          },
                        }}
                      />
                    )}
                  />
              </Box>

              {/* Company Address */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Company Address</Typography>
                <TextField fullWidth placeholder="Company address" size="small"
                  value={formData.company_address} onChange={handleChange('company_address')} sx={inputSx} />
              </Box>

              {/* Website */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={labelSx}>Website</Typography>
                <TextField fullWidth placeholder="https://yoursite.com" size="small"
                  value={formData.website} onChange={handleChange('website')} sx={inputSx} />
              </Box>

              {/* Terms */}
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                    sx={{ color: '#D1D5DB', '&.Mui-checked': { color: 'rgba(43, 103, 176, 1)' }, p: '6px' }} />
                }
                label={
                  <Typography sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', lineHeight: 1.5, letterSpacing: '0%', color: '#374151' }}>
                    I agree to the{' '}
                    <Link href="#" sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', color: 'rgba(43, 103, 176, 1)', textDecoration: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'auto' }}>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link href="#" sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12px', color: 'rgba(43, 103, 176, 1)', textDecoration: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'auto' }}>
                      Privacy Policy
                    </Link>.
                  </Typography>
                }
                sx={{ mb: 1, alignItems: 'center', '& .MuiFormControlLabel-label': { pt: 0 } }}
              />

              {/* Buttons row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: colGap }}>
                <Button variant="outlined" onClick={() => setStep(2)}
                  startIcon={<ArrowBack fontSize="small" />}
                  sx={{
                    height: '48px', minWidth: 0, px: { xs: 1, sm: 2 }, borderColor: 'rgba(217, 217, 217, 1)', color: 'rgba(0,0,0,0.7)',
                    borderRadius: '11px', textTransform: 'none', fontWeight: 600,
                    fontSize: '0.85rem', boxShadow: 'none',
                    '&:hover': { borderColor: '#C4C4C4', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Back
                </Button>
                <Button fullWidth type="submit" variant="contained" disabled={loading}
                  sx={{
                    height: '48px',
                    background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
                    borderRadius: '11px',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                      boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': { background: '#93C5FD', boxShadow: 'none' },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                      Creating account...
                    </Box>
                  ) : 'Create Account'}
                </Button>
              </Box>
            </Box>
          </>
        )}

        {step === 4 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '22px', md: '26px' },
                  lineHeight: { xs: '32px', md: '40px' },
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                }}
              >
                Verify your email
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '150%',
                  color: 'rgba(87, 81, 81, 1)',
                  mt: 0.5,
                }}
              >
                We emailed a verification code to <strong>{formData.email}</strong>. Enter it below to activate your account.
              </Typography>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleVerifyEmail(); }}>
              <Box sx={{ mb: 1 }}>
                <Typography sx={labelSx}>Verification code</Typography>
                <TextField fullWidth placeholder="Enter code" size="small" autoFocus
                  value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)}
                  inputProps={{ maxLength: 10, inputMode: 'numeric', autoComplete: 'one-time-code' }}
                  sx={inputSx} />
              </Box>

              <Typography
                onClick={handleResendVerification}
                sx={{
                  fontSize: '0.78rem', color: 'rgba(43,103,176,1)', fontWeight: 600,
                  cursor: resendLoading || resendWait.remaining > 0 ? 'default' : 'pointer',
                  opacity: resendLoading || resendWait.remaining > 0 ? 0.6 : 1,
                  mb: 2, '&:hover': { textDecoration: resendLoading || resendWait.remaining > 0 ? 'none' : 'underline' },
                }}
              >
                {resendLoading ? 'Sending new code…' : resendWait.remaining > 0 ? `Resend code in ${resendWait.remaining}s` : 'Resend code'}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: colGap }}>
                <Button variant="outlined" onClick={() => setStep(3)}
                  startIcon={<ArrowBack fontSize="small" />}
                  sx={{
                    height: '43px', minWidth: 0, px: { xs: 1, sm: 2 }, borderColor: 'rgba(217, 217, 217, 1)', color: 'rgba(0,0,0,0.7)',
                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                    fontSize: '0.85rem', boxShadow: 'none',
                    '&:hover': { borderColor: '#C4C4C4', backgroundColor: '#F9FAFB', boxShadow: 'none' },
                  }}
                >
                  Back
                </Button>
                <Button fullWidth type="submit" variant="contained" disabled={verifyLoading}
                  sx={{
                    height: '43px',
                    backgroundColor: 'rgba(43, 103, 176, 1)',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
                    '&:disabled': { backgroundColor: '#93C5FD' },
                  }}
                >
                  {verifyLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                      Verifying...
                    </Box>
                  ) : 'Verify Email'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          display: { xs: 'none', lg: 'block' },
          width: 'min(45vw, calc(100vh * 1576 / 2048))',
          height: '100vh',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
        />
        {/* Continue the left panel's exact texture (color, dot-grid, glow) across the seam,
            masked to fade out — so the boundary reads as one continuous surface, not a cut */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 0% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, 26px 26px',
          backgroundPosition: 'left center, 0 0',
          backgroundRepeat: 'no-repeat, repeat',
          WebkitMaskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          maskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          pointerEvents: 'none',
        }} />
        {/* Deepen the shadows toward the brand navy so the photo reads darker and richer */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(200deg, rgba(15,29,48,0.32) 0%, rgba(15,29,48,0.05) 55%, rgba(15,29,48,0.22) 100%)',
          pointerEvents: 'none',
        }} />
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'success' ? 3000 : 6000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontFamily: '"Montserrat", sans-serif', fontSize: '0.85rem' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignupPage;
